import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import apiClient from '@/utils/api';
import { toast } from 'sonner';
import { initiateServicePayment } from '@/utils/razorpay';
import { getUser } from '@/utils/auth';
import { useNavigate } from 'react-router-dom';
import LocationSearch from '@/components/LocationSearch';
import { 
  Scale, FileSearch, Shield, CheckCircle, ArrowRight, Phone,
  FileText, Building2, MapPin, AlertTriangle, Upload, ClipboardCheck, FileCheck, Loader2
} from 'lucide-react';

// Process steps with icons for the animated trail
const processSteps = [
  { step: 1, title: 'Submit Details', description: 'Share property details', icon: Upload },
  { step: 2, title: 'Document Collection', description: 'We gather documents', icon: FileText },
  { step: 3, title: 'Legal Analysis', description: 'Expert verification', icon: ClipboardCheck },
  { step: 4, title: 'Report Delivery', description: 'Get detailed report', icon: FileCheck },
];

// ProcessTrail Component - matches Home page style
const ProcessTrail = ({ steps, activeStep = 0 }) => {
  const progressPercent = Math.min((activeStep / (steps.length - 1)) * 100, 100);
  
  return (
    <div className="relative" data-testid="legal-process-trail">
      {/* Trail Line */}
      <div className="absolute top-6 left-0 right-0 h-1 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-primary to-amber-500 rounded-full transition-all duration-500"
          style={{ width: `${progressPercent}%`, maxWidth: '100%' }}
        />
      </div>
      
      {/* Steps */}
      <div className="relative flex justify-between">
        {steps.map((item, idx) => (
          <div 
            key={idx} 
            className={`flex flex-col items-center text-center transition-all duration-300 ${idx <= activeStep ? 'opacity-100' : 'opacity-50'}`}
          >
            <div 
              className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-all duration-300 ${
                idx < activeStep 
                  ? 'bg-primary text-white' 
                  : idx === activeStep 
                    ? 'bg-amber-500 text-white ring-4 ring-amber-200 scale-110' 
                    : 'bg-gray-100 text-gray-400'
              }`}
            >
              {idx < activeStep ? (
                <CheckCircle className="w-6 h-6" />
              ) : (
                <item.icon className="w-5 h-5" />
              )}
            </div>
            <h4 className="font-semibold text-sm mb-1">{item.title}</h4>
            <p className="text-xs text-gray-500 max-w-[100px] hidden md:block">{item.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const benefits = [
  'Protect yourself from legal disputes',
  'Verify clear property title',
  'Check for pending litigations',
  'Confirm building approvals and permits',
  'Review encumbrance certificates',
  'Expert legal guidance throughout'
];

// Service icons mapping
const serviceIcons = {
  title_verification: FileSearch,
  document_review: FileText,
  due_diligence: Building2,
  legal_opinion: Shield
};

const GetLegalOpinion = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [payingService, setPayingService] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [legalServices, setLegalServices] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    propertyAddress: '',
    propertyType: 'residential',
    serviceType: 'title_verification',
    message: ''
  });

  // Fetch pricing based on city
  const fetchPricing = async (city) => {
    setPricingLoading(true);
    try {
      const response = await apiClient.get(`/services-pricing/legal?city=${encodeURIComponent(city || '')}`);
      // Map service_id to id for compatibility
      const services = (response.data.services || []).map(s => ({
        id: s.service_id,
        title: s.name,
        description: s.description,
        price: s.price
      }));
      setLegalServices(services);
    } catch (error) {
      // Fallback to default pricing if API fails
      setLegalServices([
        { id: 'title_verification', title: 'Title Verification', description: 'Complete ownership history and encumbrance check', price: 2999 },
        { id: 'document_review', title: 'Document Review', description: 'Review of sale deed, agreements, and legal documents', price: 1999 },
        { id: 'due_diligence', title: 'Property Due Diligence', description: 'Comprehensive legal audit of the property', price: 4999 },
        { id: 'legal_opinion', title: 'Legal Opinion Report', description: 'Detailed report on property legality and risks', price: 3499 },
      ]);
    } finally {
      setPricingLoading(false);
    }
  };

  // Load default pricing on mount
  useEffect(() => {
    fetchPricing('');
  }, []);

  // Update pricing when city changes
  const handleCityChange = (city) => {
    setSelectedCity(city);
    setFormData({...formData, city});
    if (city.length >= 3) {
      fetchPricing(city);
    }
  };

  // Animate process steps
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep(prev => (prev + 1) % (processSteps.length + 1));
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.propertyAddress || !formData.city) {
      toast.error('Please fill in all required fields including city');
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post('/leads/legal-opinion', {
        name: formData.name,
        phone: formData.phone,
        email: formData.email || null,
        city: formData.city,
        property_address: formData.propertyAddress,
        property_type: formData.propertyType,
        service_type: formData.serviceType,
        message: formData.message || null
      });
      
      toast.success(`Request submitted! Your Request ID: ${response.data.request_id}`);
      
      const whatsappMsg = `Hi ZenStudios! I need legal opinion services. My Request ID: ${response.data.request_id}. Service: ${formData.serviceType}`;
      window.open(`https://wa.me/918790460259?text=${encodeURIComponent(whatsappMsg)}`, '_blank');
      
      setFormData({
        name: '',
        phone: '',
        email: '',
        city: '',
        propertyAddress: '',
        propertyType: 'residential',
        serviceType: 'title_verification',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="legal-opinion-page">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-primary/5 via-white to-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Scale className="w-4 h-4" /> Legal Services
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Get Expert Legal Opinion Before You Buy
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Protect your investment with comprehensive property verification. Our panel of 
                legal experts will review all aspects of your property purchase to ensure a 
                safe and secure transaction.
              </p>
              <div className="flex gap-4 flex-wrap">
                <Button 
                  size="lg"
                  className="rounded-full bg-primary hover:bg-primary/90 px-8"
                  onClick={() => document.getElementById('request-form')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Request Legal Opinion <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8"
                  onClick={() => window.open('https://wa.me/918790460259?text=Hi%20ZenStudios!%20I%20need%20legal%20help%20for%20my%20property%20purchase.', '_blank')}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Talk to Expert
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="bg-white p-8 rounded-2xl shadow-xl">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                    <Shield className="w-8 h-8 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">500+ Properties Verified</h3>
                    <p className="text-gray-600">Trusted legal services</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary" />
                    <span>Experienced Property Lawyers</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary" />
                    <span>Quick Turnaround (3-5 days)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary" />
                    <span>Comprehensive Reports</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary" />
                    <span>Pan-India Coverage</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Trail - Animated like Home page */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">How It Works</h2>
            <p className="text-gray-600">Simple 4-step process to get your legal opinion</p>
          </div>
          <ProcessTrail steps={processSteps} activeStep={activeStep} />
        </div>
      </section>

      {/* Services with Dynamic Pricing */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">Our Legal Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto mb-6">
              Choose from our range of property legal services designed to give you peace of mind
            </p>
            {/* City selector for pricing */}
            <div className="max-w-xs mx-auto">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full border">
                <MapPin className="w-4 h-4 text-primary" />
                <Input
                  placeholder="Enter city for pricing..."
                  value={selectedCity}
                  onChange={(e) => handleCityChange(e.target.value)}
                  className="border-0 focus-visible:ring-0 px-0"
                />
              </div>
              {selectedCity && (
                <p className="text-xs text-gray-500 mt-2">Showing prices for {selectedCity || 'all cities'}</p>
              )}
            </div>
          </div>
          
          {pricingLoading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-500">Loading pricing...</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {legalServices.map((service, idx) => {
                const IconComponent = serviceIcons[service.id] || FileText;
                return (
                  <div key={idx} className="bg-white p-6 rounded-xl hover:shadow-lg transition-shadow border">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                      <IconComponent className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{service.title}</h3>
                    <p className="text-sm text-gray-600 mb-4">{service.description}</p>
                    <p className="text-2xl font-bold text-primary">{formatCurrency(service.price)}</p>
                    <p className="text-xs text-gray-400 mt-1">+ GST applicable</p>
                    <Button
                      className="w-full mt-4 rounded-lg"
                      size="sm"
                      disabled={payingService === service.id}
                      data-testid={`pay-legal-${service.id}`}
                      onClick={async () => {
                        const user = getUser();
                        if (!user) {
                          toast.error('Please login or register to book this service');
                          navigate('/login');
                          return;
                        }
                        setPayingService(service.id);
                        try {
                          await initiateServicePayment({
                            amount: service.price,
                            description: `Legal Service - ${service.title}`,
                            userName: user.name || user.full_name,
                            userEmail: user.email,
                            userPhone: user.phone,
                            metadata: {
                              type: 'legal_service',
                              service_id: service.id,
                              service_title: service.title,
                              city: selectedCity || 'N/A'
                            },
                            onSuccess: () => {
                              toast.success(`${service.title} booked! Our legal team will contact you within 24-48 hours.`);
                            },
                            onFailure: (data) => {
                              toast.error(data.error || 'Payment failed. Please try again.');
                            },
                            onDismiss: () => toast.info('Payment cancelled'),
                          });
                        } catch (err) {
                          if (err.message !== 'Payment cancelled by user') {
                            toast.error(err.message || 'Payment failed');
                          }
                        } finally {
                          setPayingService(null);
                        }
                      }}
                    >
                      {payingService === service.id ? (
                        <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Processing...</>
                      ) : (
                        <>Pay & Book</>
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Why Important */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Why Legal Verification is Crucial</h2>
              <p className="text-gray-600 mb-8">
                Property fraud and disputes are common in India. A thorough legal check can 
                save you from years of litigation and financial loss.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-amber-50 p-8 rounded-2xl border border-amber-200">
              <div className="flex items-start gap-4 mb-6">
                <AlertTriangle className="w-8 h-8 text-amber-600 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold mb-2">Did You Know?</h3>
                  <p className="text-gray-600">
                    Over 66% of civil cases in Indian courts are property-related disputes. 
                    Many of these could have been avoided with proper legal due diligence.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="text-center p-4 bg-white rounded-xl">
                  <p className="text-3xl font-bold text-primary">66%</p>
                  <p className="text-sm text-gray-600">Property Cases</p>
                </div>
                <div className="text-center p-4 bg-white rounded-xl">
                  <p className="text-3xl font-bold text-primary">10+ yrs</p>
                  <p className="text-sm text-gray-600">Avg Litigation Time</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Request Form */}
      <section className="py-20 bg-gray-50" id="request-form">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Request Legal Opinion</h2>
            <p className="text-gray-600">
              Share your property details and our legal experts will get in touch
            </p>
          </div>
          
          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Your name"
                    required
                    className="rounded-xl mt-1"
                    data-testid="legal-name-input"
                  />
                </div>
                <div>
                  <Label>Phone Number *</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+91 98765 43210"
                    required
                    className="rounded-xl mt-1"
                    data-testid="legal-phone-input"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="your@email.com"
                    className="rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label>City *</Label>
                  <LocationSearch
                    value={formData.city}
                    onChange={(val) => handleCityChange(val)}
                    placeholder="Search city..."
                    types="city,town"
                    required
                    className="rounded-xl mt-1"
                    data-testid="legal-opinion-city"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Property Type</Label>
                  <Select value={formData.propertyType} onValueChange={(v) => setFormData({...formData, propertyType: v})}>
                    <SelectTrigger className="rounded-xl mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="residential">Residential</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="agricultural">Agricultural Land</SelectItem>
                      <SelectItem value="plot">Plot / Land</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Service Required</Label>
                  <Select value={formData.serviceType} onValueChange={(v) => setFormData({...formData, serviceType: v})}>
                    <SelectTrigger className="rounded-xl mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="title_verification">Title Verification</SelectItem>
                      <SelectItem value="document_review">Document Review</SelectItem>
                      <SelectItem value="due_diligence">Property Due Diligence</SelectItem>
                      <SelectItem value="legal_opinion">Full Legal Opinion</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Property Address *</Label>
                <Input
                  value={formData.propertyAddress}
                  onChange={(e) => setFormData({...formData, propertyAddress: e.target.value})}
                  placeholder="Full property address"
                  required
                  className="rounded-xl mt-1"
                  data-testid="legal-address-input"
                />
              </div>

              <div>
                <Label>Additional Information</Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Any specific concerns or documents you have..."
                  rows={3}
                  className="rounded-xl mt-1"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full rounded-full py-6 text-lg bg-primary hover:bg-primary/90" 
                disabled={loading}
                data-testid="legal-submit-btn"
              >
                {loading ? 'Submitting...' : 'Request Legal Opinion'}
              </Button>
              
              <p className="text-center text-sm text-gray-500">
                Our legal team will contact you within 24 hours to discuss your requirements.
              </p>
              
              <p className="text-center text-xs text-gray-400 border-t pt-4">
                * Prices shown are indicative and may vary based on property location and complexity. 
                All payments are non-refundable once service delivery begins.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GetLegalOpinion;
